import type { UseQueryOptions } from '@tanstack/react-query'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  GetAllInstansiTypeResponseDataType,
  InfiniteQueryOptions,
  InstansiTypeResponseDataType
} from './types'

const QUERY_KEY = (params: {
  page: number
  per_page: number
  search: string
}) => ['reference_instansi', { ...params }] as const

type InstansiTypeQueryKey = ReturnType<typeof QUERY_KEY>

const getBatch = (
  opts?: Partial<{
    page: number
    per_page: number
    search: string
  }>
): InfiniteQueryOptions<InstansiTypeQueryKey> => {
  const params = {
    page: opts?.page ?? 1,
    per_page: opts?.per_page ?? 10,
    search: opts?.search ?? ''
  }

  return {
    queryKey: QUERY_KEY(params),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const meta = lastPage.meta

      if (!meta) return undefined
      return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
        ? (meta?.current_page ?? 0) + 1
        : undefined
    },
    queryFn: async ({ pageParam }) => {
      try {
        const res = await api.get<GetAllInstansiTypeResponseDataType>(
          '/report/reference/instansi',
          {
            params: {
              page: pageParam,
              per_page: params.per_page,
              name:
                params.search.length > 3
                  ? `%${params.search.toUpperCase()}%`
                  : undefined
            }
          }
        )

        return {
          message: res.data.message,
          data: res.data.data,
          meta: res.data.meta
        }
      } catch (ex) {
        throw handleAxiosError(ex)
      }
    }
  }
}

const INSTANSI_TYPE_BY_ID_QUERY_KEY = (params: { id: string }) =>
  ['reference_instansi', params] as const

const byId = (opts: {
  id: string
}): UseQueryOptions<InstansiTypeResponseDataType> => ({
  queryKey: INSTANSI_TYPE_BY_ID_QUERY_KEY(opts),
  queryFn: async () => {
    try {
      const res = await api.get<InstansiTypeResponseDataType>(
        `/report/reference/instansi/${opts.id}`
      )

      const data = res.data.data
      if (!data) {
        throw new Error(`ID instansi ${opts.id} tidak dapat ditemukan.`)
      }

      return {
        message: res.data.message,
        data
      }
    } catch (ex) {
      throw handleAxiosError(ex)
    }
  }
})

export const instansiReference = {
  queryKey: {
    all: QUERY_KEY,
    byId: INSTANSI_TYPE_BY_ID_QUERY_KEY
  },
  batched: getBatch,
  byId
}
